/**
 * Hook to fetch plan limits and current usage for the company.
 * Used to disable "Add" buttons when limits are exhausted.
 */
import { useApiQuery } from './useApiQuery';
import { useAuth } from '../contexts/AuthContext';

interface PlanUsageData {
  planName: string;
  limits: {
    maxServers: number;
    maxUsers: number;
    maxGithubTokens: number;
    maxAwsAccounts: number;
    dataRetentionDays: number;
  };
  usage: {
    servers: number;
    users: number;
    githubTokens: number;
    awsAccounts: number;
  };
}

export function usePlanUsage() {
  const { user } = useAuth();
  const companyId = user?.companyId;

  const { data } = useApiQuery<PlanUsageData>(
    ['plan-usage', companyId],
    `/companies/${companyId}/plan-usage`,
    { enabled: !!companyId, staleTime: 60_000 },
  );

  const isLimitReached = (resource: 'servers' | 'users' | 'githubTokens' | 'awsAccounts'): boolean => {
    if (!data) return false;
    const limitKey = resource === 'servers' ? 'maxServers'
      : resource === 'users' ? 'maxUsers'
      : resource === 'githubTokens' ? 'maxGithubTokens'
      : 'maxAwsAccounts';
    const limit = data.limits[limitKey];
    if (limit === -1) return false; // unlimited
    return data.usage[resource] >= limit;
  };

  return { planUsage: data, isLimitReached };
}
